import {Component, Injector, OnInit} from '@angular/core';
import {BaseComponent} from '../../base.component';
import {FormControl} from '@angular/forms';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {RequestType} from '../../../enums/request.type';
import {ListResponse} from '../../../network/service/list.response';
import {DataResponse} from '../../../network/service/data.response';
import {SnackBarUtil} from '../../../util/snack.bar.util';
import {StringUtil} from '../../../util/string.util';
import {Config} from '../../../common/config';
import {RouteUtil} from '../../../util/route.util';
import {ChooserOptions} from '../dialog/chooser/options/chooser.options';
import {ChooserComponent} from '../dialog/chooser/chooser.component';
import {strings} from '../../../../value/strings';

@Component({
  selector: 'app-place-order',
  standalone: false,
  templateUrl: './place-order.component.html',
  styleUrls: ['./place-order.component.scss']
})
export class PlaceOrderComponent extends BaseComponent implements OnInit {
  userId?: string;
  user?: any;
  categories?: any[] = [];
  tags: any[] = [];
  selectedCategoryId?: string;
  selectedTagId?: string;
  products: any[] = [];
  allProducts: any[] = [];
  searchControl = new FormControl('');
  isSearching = false;
  showSearchResults = false;
  isLoadingCategories = false;
  orderItems: any = [];
  orderNotes = '';
  paymentMethods?: any[];
  deliveryMethods?: any[];
  selectedUserAddress?: any;
  selectedDeliveryMethod?: any;
  selectedPaymentType?: any;
  selectedSpace?: any;
  selectedPos?: any;
  userAddresses?: any[];
  branches?: any[];
  preFactor?: any;
  existingFactorId?: string;
  isAddToExistingMode = false;
  isNewCustomerMode = false;
  mobileNumber = '';
  newCustomerInfo: any = {
    firstName: '',
    lastName: '',
    birthDate: ''
  };
  foundUser: any = null;
  isSearchingUser = false;
  searchedUser = false;
  customerInfo: any = {};
  branchId?: string;
  protected readonly StringUtil = StringUtil;
  protected readonly Config = Config;

  constructor(injector: Injector) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.activatedRoute.params.subscribe(params => {
      this.userId = params['userId'];
      if (this.userId) {
        this.loadCustomerInfo();
      } else {
        this.isNewCustomerMode = true;
      }
    });
    // Check for query parameters (existing factor mode)
    this.activatedRoute.queryParams.subscribe(queryParams => {
      if (queryParams['factorId'] && queryParams['mode'] === 'add-to-existing') {
        this.existingFactorId = queryParams['factorId'];
        this.isAddToExistingMode = true;
        this.loadExistingFactor();
      }
    });
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value && value.length > 0) {
        this.searchProducts(value);
      } else {
        this.showSearchResults = false;
        this.loadProductsBySelection();
      }
    });
  }

  override loadOnline() {
    super.loadOnline();
    this.mobileNumber = '09353921811';
    this.searchUserByMobile();
  }

  searchUserByMobile() {
    if (!this.mobileNumber || this.mobileNumber.length < 10) {
      SnackBarUtil.makeMessage(this, 'لطفا شماره موبایل معتبر وارد کنید', false);
      return;
    }
    this.isSearchingUser = true;
    this.searchedUser = true;
    this.foundUser = null;
    this.serviceApi.getClients(undefined, undefined, 'UserAttributes,UserAttributes.Attribute,UserAttributes.Attribute.AttributeType,UserExtraAttributes,UserExtraAttributes.Attribute,UserExtraAttributes.Attribute.AttributeType', RequestType.ONE, {
      Filter: {
        Values: [{
          Field: 'MobileNumber',
          Type: 'EQ',
          Value: this.mobileNumber
        }],
        Type: 'AND'
      }
    }).subscribe((res: DataResponse<any>) => {
      this.isSearchingUser = false;
      if (res.success && res.data) {
        this.foundUser = res.data;
        this.userId = res.data.UserId;
        this.customerInfo = {
          name: `${res.data?.FirstName || ''} ${res.data?.LastName || ''}`,
          phone: res.data?.MobileNumber || '',
          birthDate: res.data?.BirthDate,
          registrationDate: res.data?.CreateDate
        };
      } else {
        this.foundUser = null;
      }
      this.serviceApi.getBranches(undefined, undefined, undefined, RequestType.ALL, undefined).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          this.branches = res.data;
          if (this.branches && this.branches.length > 0) {
            this.onBranchClick(this.branches[0]);
          }
        }
      });
    });
  }

  onBranchClick(branch: any) {
    if (this.branchId !== branch.BusinessId) {
      this.branchId = branch.BusinessId;
      this.selectedCategoryId = undefined;
      this.selectedTagId = undefined;
      this.showSearchResults = false;
      this.searchControl.setValue('');
      this.products = [];
      this.allProducts = [];
      this.categories = [];
      this.paymentMethods = [];
      this.tags = [];
      this.orderItems = [];
      this.loadCategoriesAndTags();
      this.loadOrders();
    }
  }

  loadCategoriesAndTags() {
    this.isLoadingCategories = true;
    this.serviceApi.getCategoryAndTags(undefined, undefined, undefined, RequestType.ALL, this.branchId, {
      Order: {
        Field: 'Order',
        Type: 'ASC'
      }
    }).subscribe({
      next: (res: ListResponse<any>) => {
        if (res.success && res.data) {
          const data = res.data as any;
          this.categories = data.Categories || [];
          this.tags = data.Tags || [];
          if (this.categories && this.categories.length > 0) {
            this.selectedCategoryId = this.categories[0].CategoryId;
          } else if (this.tags && this.tags.length > 0) {
            this.selectedTagId = this.tags[0].TagId;
          }
          this.loadAllProducts();
        }
        this.isLoadingCategories = false;
      },
      error: () => {
        this.isLoadingCategories = false;
      }
    });
  }

  loadAllProducts() {
    let filter: any = null;
    if (this.selectedTagId) {
      filter = {
        Values: [{
          Field: 'TagId',
          Type: 'EQ',
          Value: this.selectedTagId
        }]
      };
    } else if (this.selectedCategoryId) {
      filter = {
        Values: [{
          Field: 'CategoryId',
          Type: 'EQ',
          Value: this.selectedCategoryId
        }]
      };
    }
    const filterParam = filter ? {Filter: filter} : {};
    this.serviceApi.getServices(undefined, undefined, 'ServicePrices,ServicePrices.ServicePriceAdditives,ServicePrices.ServicePriceAdditives.Additive,ServicePrices.ServicePriceAdditives.Attribute,ServicePrices.ServicePriceAdditives.Attribute.AttributeType,Category,Tag', RequestType.ALL, this.branchId, filterParam).subscribe({
      next: (res: ListResponse<any>) => {
        if (res.success) {
          this.allProducts = res.data || [];
          this.products = res.data || [];
        }
      }
    });
  }

  loadOrders(withLoadDeliveryMethod: boolean = true) {
    this.serviceApi.getOrders(undefined, undefined, 'ServicePrice,ServicePrice.Service,ServicePrice.ServicePriceAdditives,ServicePrice.ServicePriceAdditives.Additive', RequestType.ALL, {
      Filter: {
        Values: [{
          Field: 'UserId',
          Type: 'EQ',
          Value: this.userId
        }, {
          Field: 'Type',
          Type: 'EQ',
          Value: strings.staff_basket
        }, {
          Field: 'BranchId',
          Type: 'EQ',
          Value: this.branchId
        }],
        Type: 'AND'
      }
    }).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.orderItems = res.data;
        if (this.orderItems.length > 0 && withLoadDeliveryMethod) {
          this.loadDeliveryMethods();
        }
      }
    });
  }

  loadDeliveryMethods() {
    this.serviceApi.getDeliveryMethods(undefined, undefined, undefined, RequestType.ALL, {
      Filter: {},
      Order: {
        Field: 'Title',
        Type: 'ASC'
      }
    }, this.branchId).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.deliveryMethods = res.data;
        if ((this.deliveryMethods ?? []).length > 0) {
          this.onChangeDeliveryMethod(this.deliveryMethods![0]);
        }
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  onChangeDeliveryMethod(deliveryMethod: any) {
    if (!this.selectedDeliveryMethod || this.selectedDeliveryMethod.DeliveryMethodId !== deliveryMethod.DeliveryMethodId) {
      this.selectedDeliveryMethod = deliveryMethod;
      if (deliveryMethod.Type === 'OUTSIDE_DELIVER') {
        this.serviceApi.getUserAddresses(0, 0, '', 'ONE', {
          Filter: {
            Values: [{
              Field: 'DeliveryMethodId',
              Type: 'EQ',
              Value: this.selectedDeliveryMethod.DeliveryMethodId
            }, {
              Field: 'UserId',
              Type: 'EQ',
              Value: this.foundUser.UserId
            }]
          },
          Order: {
            Field: 'Title',
            Type: 'ASC'
          }
        }).subscribe((res: DataResponse<any>) => {
          if (res.success) {
            this.selectedUserAddress = res.data;
          }
        });
      } else {
        this.selectedUserAddress = undefined;
      }
      this.getPreFactor(this.selectedDeliveryMethod.DeliveryMethodId);
      this.changePaymentMethod(this.selectedDeliveryMethod);
    }
  }

  getPreFactor(deliveryMethodeId?: any) {
    this.serviceApi.getPreFactor({
      DeliveryMethodId: deliveryMethodeId,
      UserId: this.userId
    }).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.preFactor = res.data;
      }
    });
  }

  changePaymentMethod(deliveryMethod?: any) {
    this.serviceApi.getPaymentMethode(undefined, undefined, undefined, RequestType.ALL, {
      Filter: {
        Type: "AND",
        Values: [{
          Field: "IncludePaymentMethodIds",
          Type: "IN",
          Value: deliveryMethod.PaymentMethodIds
        }]
      },
      Order: {
        Field: 'Title',
        Type: 'ASC'
      }
    }, this.branchId).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.paymentMethods = res.data;
        if (this.paymentMethods!.length > 0) {
          this.onChangePaymentMethod(this.paymentMethods![0]);
        }
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  openChangeAddressClicked(deliveryMethod: any) {
    let chooserOptions = new ChooserOptions();
    let chooseAddressComponent = this.matDialog.open(ChooserComponent, {
      width: '1284px',
      height: '60%',
      data: {
        chooserType: 'USER_ADDRESS',
        DeliveryMethod: deliveryMethod,
        UserId: this.foundUser.UserId,
        UserAddress: this.selectedUserAddress
      }
    });
    chooserOptions.onChoose = (userAddress: any) => {
      this.selectedUserAddress = userAddress;
    };
    chooseAddressComponent.componentInstance.chooserOptions = chooserOptions;
  }

  openChangeSpaceClicked() {
    let chooserOptions = new ChooserOptions();
    let chooseAddressComponent = this.matDialog.open(ChooserComponent, {
      width: '1284px',
      height: '60%',
      data: {
        chooserType: 'SPACE'
      }
    });
    chooserOptions.onChoose = (space: any) => {
      this.selectedSpace = space;
    };
    chooseAddressComponent.componentInstance.chooserOptions = chooserOptions;
  }

  onChangePaymentMethod(paymentMethod: any) {
    this.selectedPaymentType = paymentMethod;
    this.selectedPos = undefined;
    switch (paymentMethod.Type) {
      case 'POS':
        this.selectedPos = paymentMethod.Metadata.Poses[0];
        break;
      case 'CASH':
      case 'WALLET':
        break;
      case 'CARD_TO_CARD':
      case 'PSP':
        break;
    }
  }

  searchProducts(query: string) {
    const branchId = this.branchId;
    if (!branchId) {
      this.isSearching = false;
      return;
    }
    this.isSearching = true;
    this.showSearchResults = true;
    const searchFilter = {
      Values: [{
        Field: 'Title',
        Type: 'LIKE',
        Value: '%' + query + '%'
      }]
    };
    this.serviceApi.getServices(undefined, undefined, 'ServicePrices,ServicePrices.ServicePriceAdditives,ServicePrices.ServicePriceAdditives.Additive,ServicePrices.ServicePriceAdditives.Attribute,ServicePrices.ServicePriceAdditives.Attribute.AttributeType,Category,Tag', RequestType.ALL, branchId, {
      Filter: searchFilter
    }).subscribe({
      next: (res: ListResponse<any>) => {
        if (res.success) {
          this.products = res.data || [];
        }
        this.isSearching = false;
      }
    });
  }

  onCategoryClick(category: any) {
    if (category.CategoryId !== this.selectedCategoryId) {
      this.selectedCategoryId = category.CategoryId;
      this.selectedTagId = undefined;
      this.showSearchResults = false;
      this.searchControl.setValue('');
      this.loadAllProducts();
    }
  }

  onTagClick(tag: any) {
    if (tag.TagId !== this.selectedTagId) {
      this.selectedTagId = tag.TagId;
      this.selectedCategoryId = undefined;
      this.showSearchResults = false;
      this.searchControl.setValue('');
      this.loadProductsBySelection();
    }
  }

  clearSearch() {
    this.searchControl.setValue('');
    this.showSearchResults = false;
    this.loadProductsBySelection();
  }

  loadProductsBySelection() {
    if (this.selectedTagId || this.selectedCategoryId) {
      this.loadAllProducts();
    }
  }

  addToOrder(servicePriceId: any, count?: number) {
    if (count === undefined) {
      count = this.orderItems.find((orderItem: any) => orderItem.ServicePriceId === servicePriceId)?.Count + 1;
      if (!count) {
        count = 1;
      }
    }
    this.serviceApi.postOrders({
      ServicePriceId: servicePriceId,
      Count: count,
      ServicePriceAdditiveIds: [],
      Type: strings.staff_basket,
      UserId: this.userId,
      BranchId: this.branchId
    }).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.loadOrders(false);
        this.getPreFactor(this.selectedDeliveryMethod.DeliveryMethodId);
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  confirmOrder() {
    if (this.orderItems.length === 0) {
      SnackBarUtil.makeMessage(this, 'لطفا محصولی به سفارش اضافه کنید', false);
      return;
    }
    if (!this.selectedDeliveryMethod) {
      SnackBarUtil.makeMessage(this, 'لطفا روش تحویل را انتخاب کنید', false);
      return;
    } else if (this.selectedDeliveryMethod.Type === 'OUTSIDE_DELIVER' && !this.selectedUserAddress) {
      SnackBarUtil.makeMessage(this, 'لطفا آدرس را انتخاب کنید', false);
      return;
    } else if (this.selectedDeliveryMethod.Type === 'INSIDE' && !this.selectedSpace) {
      SnackBarUtil.makeMessage(this, 'لطفا میز را انتخاب کنید', false);
      return;
    }
    if (!this.selectedPaymentType) {
      SnackBarUtil.makeMessage(this, 'لطفا روش پرداخت را انتخاب کنید', false);
      return;
    }
    if (this.isAddToExistingMode && this.existingFactorId) {
      // Add items to existing factor
      this.addItemsToExistingFactor();
    } else {
      // Create new factor
      this.createNewFactor();
    }
  }

  createNewFactor() {
    this.serviceApi.createFactor({
      DeliveryMethodId: this.selectedDeliveryMethod.DeliveryMethodId,
      UserAddressId: this.selectedUserAddress ? this.selectedUserAddress.UserAddressId : undefined,
      SpaceId: this.selectedSpace ? this.selectedSpace.SpaceId : undefined,
      PaymentMethodId: this.selectedPaymentType.PaymentMethodId,
      UserId: this.userId,
      Description: this.orderNotes,
      BranchId: this.branchId
    }).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        SnackBarUtil.makeMessage(this, 'سفارش با موفقیت ثبت شد', true);
        if (this.isNewCustomerMode) {
          this.router.navigate([RouteUtil.DASHBOARD_MODULE]).then();
        } else {
          this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.USER_DETAIL_COMPONENT, {UserId: this.userId}]).then();
        }
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  addItemsToExistingFactor() {
    this.serviceApi.createFactor({}).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        SnackBarUtil.makeMessage(this, 'محصولات با موفقیت به فاکتور اضافه شدند', true);
        if (this.isNewCustomerMode) {
          this.router.navigate([RouteUtil.DASHBOARD_MODULE]).then();
        } else {
          this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.USER_DETAIL_COMPONENT, {UserId: this.userId}]).then();
        }
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  backToCustomer() {
    if (this.isNewCustomerMode) {
      // Go back to dashboard home
      this.router.navigate([RouteUtil.DASHBOARD_MODULE]).then();
    } else {
      this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.USER_DETAIL_COMPONENT, {UserId: this.userId}]).then();
    }
  }

  getSelectedTitle(): string {
    if (this.showSearchResults) {
      return `نتایج جستجو برای "${this.searchControl.value}"`;
    }
    if (this.selectedTagId) {
      const tag = this.tags.find(t => t.TagId === this.selectedTagId);
      return tag?.Title || '';
    }
    if (this.selectedCategoryId && this.categories) {
      const category = this.categories.find(c => c.CategoryId === this.selectedCategoryId);
      return category?.Title || '';
    }
    return 'همه دسته‌بندی‌ها';
  }

  openChangePosClicked(paymentMethod: any) {
    let chooserOptions = new ChooserOptions();
    let chooseAddressComponent = this.matDialog.open(ChooserComponent, {
      width: '1284px',
      height: '60%',
      data: {
        chooserType: 'POS',
        Poses: paymentMethod.Metadata.Poses,
        Pos: this.selectedPos
      }
    });
    chooserOptions.onChoose = (pos: any) => {
      this.selectedPos = pos;
    };
    chooseAddressComponent.componentInstance.chooserOptions = chooserOptions;
  }

  isBranchSelected(branch: any): boolean {
    return this.branchId === branch.BusinessId;
  }

  checkIfChange(event: any) {
    this.searchedUser = false;
  }

  createNewUser() {
    if (!this.newCustomerInfo.firstName || !this.newCustomerInfo.lastName) {
      SnackBarUtil.makeMessage(this, 'لطفا تمام فیلدهای ضروری را پر کنید', false);
      return;
    }
    const userData = {
      FirstName: this.newCustomerInfo.firstName,
      LastName: this.newCustomerInfo.lastName,
      MobileNumber: this.mobileNumber,
      UserType: 'CLIENT',
      BirthDate: this.newCustomerInfo.birthDate
    };
    this.serviceApi.createUsers(userData).subscribe((res: DataResponse<any>) => {
      if (res.success && res.data) {
        const userData = res.data.dataValues || res.data;
        this.foundUser = userData;
        this.userId = userData.UserId;
        this.mobileNumber = userData.MobileNumber;
        this.customerInfo = {
          name: `${userData?.FirstName || ''} ${userData?.LastName || ''}`,
          phone: userData?.MobileNumber || '',
          birthDate: userData?.BirthDate,
          registrationDate: userData?.CreateDate
        };
        this.loadCategoriesAndTags();
        SnackBarUtil.makeMessage(this, 'کاربر جدید با موفقیت ایجاد شد', true);
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  resetCustomerSearch() {
    this.mobileNumber = '';
    this.foundUser = null;
    this.userId = undefined;
    this.customerInfo = {};
    this.newCustomerInfo = {
      firstName: '',
      lastName: '',
      nationalCode: '',
      birthDate: ''
    };
    this.orderItems = [];
    this.categories = [];
    this.tags = [];
    this.products = [];
    this.allProducts = [];
  }

  loadExistingFactor() {
    if (!this.existingFactorId) return;
    this.serviceApi.getFactors(undefined, undefined, 'FactorItems,FactorItems.ServicePrice,FactorItems.ServicePrice.Service', RequestType.ONE, {
      Filter: {
        Values: [{
          Field: 'FactorId',
          Type: 'EQ',
          Value: this.existingFactorId
        }]
      }
    }).subscribe((res: DataResponse<any>) => {
      if (res.success && res.data) {
        const factor = res.data;
        // Load existing items into order
        this.orderItems = factor.FactorItems?.map((item: any) => ({
          product: item.ServicePrice?.Service,
          quantity: item.Count,
          price: item.ServicePrice?.Price || 0,
          totalPrice: (item.ServicePrice?.Price || 0) * item.Count
        })) || [];
        // Set order notes if exists
        this.orderNotes = factor.Notes || '';
        // Load delivery and payment methods for existing factor
        this.loadDeliveryMethods();
      }
    });
  }

  loadCustomerInfo() {
    this.serviceApi.getClients(undefined, undefined, 'UserAttributes,UserAttributes.Attribute,UserAttributes.Attribute.AttributeType,UserExtraAttributes,UserExtraAttributes.Attribute,UserExtraAttributes.Attribute.AttributeType', RequestType.ONE, {
      Filter: {
        Values: [{
          Field: 'UserId',
          Type: 'EQ',
          Value: this.userId
        }],
        Type: 'AND'
      }
    }).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.user = res.data;
        this.customerInfo = {
          name: `${this.user?.FirstName || ''} ${this.user?.LastName || ''}`,
          phone: this.user?.MobileNumber || '',
          birthDate: this.user?.BirthDate,
          registrationDate: this.user?.CreateDate
        };
      }
    });
  }
}
